import { useEffect, useRef } from 'react';
import { useAuth } from '@/shared/context/AuthContext';
import { useClinicScope } from '@/shared/context/ClinicScopeContext';
import { queryClient } from './queryClient';
import { syncClinicMasterDataQueries, syncMasterDataQueries } from './masterDataCatalog';
import {
  applyMasterDataQueryDefaults,
  clearMasterDataMemory,
  discardUnreadableMasterData,
  hydrateMasterData,
  masterDataReaderForUser,
  setMasterDataScope,
} from './masterDataStore';

const clinicIdOf = (selected: string | null | undefined): string | null =>
  selected && selected !== 'all' ? selected : null;

const permissionKeyOf = (role: string | undefined, permissions: string[] | undefined): string =>
  role === 'admin' ? 'admin' : [...(permissions ?? [])].sort().join('\n');

/**
 * Puts this user's saved catalog into memory before child screens read it,
 * drops lists they cannot read, then refreshes the rest from REST.
 * Logout keeps that user's copy. Another account does not read it.
 */
export function MasterDataSync(): null {
  const { isAuthenticated, user } = useAuth();
  const { selected } = useClinicScope();
  const organizationId = user?.organization ?? '';
  const userId = user?.id || user?._id || '';
  const permissionKey = permissionKeyOf(user?.role, user?.permissions);
  const hydratedFor = useRef<string | null>(null);
  const accessKey = useRef<string | null>(null);

  if (isAuthenticated && organizationId && userId) {
    const scopeKey = `${organizationId}:${userId}`;
    const canRead = masterDataReaderForUser(user?.role, user?.permissions);
    if (hydratedFor.current !== scopeKey) {
      hydratedFor.current = scopeKey;
      accessKey.current = permissionKey;
      setMasterDataScope({ organizationId, userId });
      applyMasterDataQueryDefaults(queryClient);
      clearMasterDataMemory(queryClient);
      const restored = hydrateMasterData(queryClient, organizationId, userId);
      discardUnreadableMasterData(
        queryClient,
        organizationId,
        userId,
        canRead,
        undefined,
        restored
      );
    } else if (accessKey.current !== permissionKey) {
      accessKey.current = permissionKey;
      discardUnreadableMasterData(queryClient, organizationId, userId, canRead);
    }
  } else if (hydratedFor.current !== null) {
    hydratedFor.current = null;
    accessKey.current = null;
    setMasterDataScope(null);
    clearMasterDataMemory(queryClient);
  }

  const boot = useRef<{ org: string; clinic: string | null; permissionKey: string } | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !organizationId || !userId) {
      boot.current = null;
      return;
    }

    const clinicId = clinicIdOf(selected);
    const canRead = masterDataReaderForUser(user?.role, user?.permissions);
    const previous = boot.current;
    boot.current = { org: organizationId, clinic: clinicId, permissionKey };
    const clinicOnly =
      previous?.org === organizationId &&
      previous.permissionKey === permissionKey &&
      previous.clinic !== clinicId;
    const task = clinicOnly
      ? syncClinicMasterDataQueries(queryClient, clinicId, canRead)
      : syncMasterDataQueries(queryClient, organizationId, userId, clinicId, canRead);

    let cancelled = false;
    void task.catch(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, organizationId, userId, permissionKey, selected, user?.role, user?.permissions]);

  return null;
}
